import * as STD      from '@emersya/mpa-std-lib';
import * as BasketNS from './';

export class Product {
	readonly basket : BasketNS.Basket;
	readonly id : string;
	readonly viewerCode : string;
	readonly title : string;
	readonly parts = <BasketNS.Part[]> [];
	reference : BasketNS.Reference | null = null;
	sku : BasketNS.Reference | null = null;
	gtin : BasketNS.Reference | null = null;
	quantity : number;
	metaData : STD.object.JSONComplexRecursive | null = null;
	thumbnailsURLs : string[] | null = null;
	configurationCode : string | null = null;
	configurationReplayURL : string | null = null;
	configuratorReplayURL : string | null = null;
	PDFURL : string | null = null;
	readonly pricing : BasketNS.Pricing.TPricing | null = null;
	unitPrice = BasketNS.Price.ofZero(); // auto-computed
	totalPrice = BasketNS.Price.ofZero(); // auto-computed
	isInitialized = false;

	setReference(reference? : string | BasketNS.Reference | null) {
		if (reference) {
			if (typeof reference === 'string') {
				this.reference = new BasketNS.Reference(reference);
			} else {
				this.reference = reference;
			}
		} else {
			this.reference = null;
		}
	}

	setSKU(sku? : string | BasketNS.Reference | null) {
		if (sku) {
			if (typeof sku === 'string') {
				this.sku = new BasketNS.Reference(sku);
			} else {
				this.sku = sku;
			}
		} else {
			this.sku = null;
		}
	}

	setGTIN(gtin? : string | BasketNS.Reference | null) {
		if (gtin) {
			if (typeof gtin === 'string') {
				this.gtin = new BasketNS.Reference(gtin);
			} else {
				this.gtin = gtin;
			}
		} else {
			this.gtin = null;
		}
	}

	async setThumbnailsURLs(thumbnailsURLs : string[] | null) {
		this.thumbnailsURLs = thumbnailsURLs ? Array.from(thumbnailsURLs) : null;
		await this.basket.dispatchEvent('basket-update', {
			subType : 'product-thumbnailsURLs',
			details : {
				product : this
			}
		});
	};

	async setConfigurationCode(configurationCode : string | null) {
		this.configurationCode = configurationCode;
		await this.basket.dispatchEvent('basket-update', {
			subType : 'product-configurationCode',
			details : {
				product : this
			}
		});
	};

	async setConfigurationReplayURL(configurationReplayURL : string | null) {
		this.configurationReplayURL = configurationReplayURL;
		await this.basket.dispatchEvent('basket-update', {
			subType : 'product-configurationReplayURL',
			details : {
				product : this
			}
		});
	};

	async setConfiguratorReplayURL(configuratorReplayURL : string | null) {
		this.configuratorReplayURL = configuratorReplayURL;
		await this.basket.dispatchEvent('basket-update', {
			subType : 'product-configuratorReplayURL',
			details : {
				product : this
			}
		});
	};

	async setPDFURL(PDFURL : string | null) {
		this.PDFURL = PDFURL;
		await this.basket.dispatchEvent('basket-update', {
			subType : 'product-PDFURL',
			details : {
				product : this
			}
		});
	};

	createPart(desc : Omit<BasketNS.Part.IDescriptor, 'parent'>) {
		const internalDescriptor = Object.assign({}, desc, {
			parent : this
		});
		if (Object.getOwnPropertyDescriptor(desc, 'title')?.get) {
			Object.defineProperty(internalDescriptor, 'title', {
				get() {
					return desc.title;
				}
			});
		}
		return new BasketNS.Part(internalDescriptor);
	};

	getPart(partsPath : string[]) {
		const [ rootPartName, ...remainingPath ] = [ ...partsPath ];
		const rootPart = this.parts.find(x => x.id === rootPartName);
		if (!rootPart) {
			throw STD.errors.user(
				`Unknown part<${ partsPath.join('.') }>!`,
				{
					rootPart,
					partsPath
				}
			);
		}
		if (remainingPath.length === 0) {
			return (rootPart);
		}
		return (rootPart.getPart(remainingPath));
	};

	serialize() : Product.ISerialized {
		return {
			name :                   this.id,
			title :                  this.title,
			reference :              this.reference?.serialize() || null,
			sku :                    this.sku?.serialize() || null,
			gtin :                   this.gtin?.serialize() || null,
			quantity :               this.quantity,
			metaData :               this.metaData ? <any> Object.assign({}, this.metaData) : null,
			parts :                  this.parts.filter(x => !x.ignoreOnSave).map(x => x.serialize()),
			unitPrice :              this.unitPrice.serialize(),
			totalPrice :             this.totalPrice.serialize(),
			thumbnailsURLs :         this.thumbnailsURLs,
			configurationCode :      this.configurationCode,
			configurationReplayURL : this.configurationReplayURL,
			configuratorReplayURL :  this.configuratorReplayURL,
			PDFURL :                 this.PDFURL
		};
	};

	/* Taxes are not correctly implemented! */
	computePrice() {
		const unitPrice = BasketNS.Price.ofZero();
		for (const part of this.parts) {
			unitPrice.add(part.computePrice());
		}
		if (this.pricing) {
			unitPrice.add(this.pricing.resolve());
		}
		this.unitPrice = unitPrice;
		const totalPrice = new BasketNS.Price(
			unitPrice.exclTax.multipliedBy(this.quantity),
			unitPrice.inclTax.multipliedBy(this.quantity),
			unitPrice.taxes
		);
		this.totalPrice = totalPrice;
		return (this.totalPrice);
	};

	async initialize() {
		if (this.isInitialized) {
			console.warn('Already initialized product!');
			return;
		}
		for (const part of this.parts) {
			await part.initialize();
		}
		this.isInitialized = true;
	};

	constructor(settings : Product.IDescriptor) {
		this.basket = settings.basket;
		this.id = settings.id;
		this.title = settings.title;
		this.viewerCode = settings.viewerCode;
		this.setReference(settings.reference);
		this.setSKU(settings.sku);
		this.setGTIN(settings.gtin);
		this.quantity = settings.quantity || 1;
		if (settings.metaData) {
			this.metaData = settings.metaData;
		}
		if (settings.pricing) {
			this.pricing = settings.pricing || null;
		}
		for (const partDesc of settings.parts) {
			this.createPart(partDesc);
		}
	};
};
export namespace Product {

	export interface IDescriptor {
		readonly basket : BasketNS.Basket;
		readonly id : string;
		readonly title : string;
		readonly reference : string | BasketNS.Reference;
		readonly viewerCode : string;
		readonly sku : string | BasketNS.Reference;
		readonly gtin : string | BasketNS.Reference | null;
		readonly quantity : number;
		readonly metaData : STD.object.JSONComplexRecursive | null;
		readonly parts : Omit<BasketNS.Part.IDescriptor, 'parent'>[];
		readonly pricing? : BasketNS.Pricing.TPricing | null;
	};

	export interface ISerialized {
		readonly name : string;
		readonly title : string;
		readonly reference : BasketNS.Reference.ISerialized | null;
		readonly sku : BasketNS.Reference.ISerialized | null;
		readonly gtin : BasketNS.Reference.ISerialized | null;
		readonly quantity : number;
		readonly metaData : Record<any, string> | null;
		readonly parts : BasketNS.Part.ISerialized[];
		readonly unitPrice : BasketNS.Price.ISerialized;
		readonly totalPrice : BasketNS.Price.ISerialized;
		readonly thumbnailsURLs : string[] | null;
		readonly configurationCode : string | null;
		readonly configurationReplayURL : string | null;
		readonly configuratorReplayURL : string | null;
		readonly PDFURL : string | null;
	};
}
